import { Chat, ChatResponseModeEnum, ChatRoleEnum } from 'types/chat'
import { ChatMessage } from 'types/chat-message'
import { DateTime } from 'luxon'
import { t } from 'i18next'
import { store } from 'store/index'
import { messagesSelectors } from 'store/messages'
import _ from 'lodash'
import { User } from 'types/user'

export const formatDateWithDurationCalculation = (dateISO: string) => {
  const now = DateTime.now()
  const luxonDate = DateTime.fromISO(dateISO)
  const isToday = luxonDate.toISODate() === now.toISODate()
  const isYesterday = luxonDate.toISODate() === now.minus({ days: 1 }).toISODate()
  const isSameWeek = luxonDate.toISODate() === now.minus({ days: 7 }).toISODate()

  if (isToday) {
    return luxonDate.toFormat('HH:mm')
  }
  if (isYesterday) {
    return t('yesterday')
  }
  if (isSameWeek) {
    return luxonDate.toFormat('EEEE')
  }
  return luxonDate.toFormat('dd/MM/yyyy')
}
export const getChatDateFormatted = (chat: Chat) => {
  const { updatedAt, createdAt } = chat
  if (updatedAt) {
    return formatDateWithDurationCalculation(updatedAt)
  }
  return formatDateWithDurationCalculation(createdAt)
}

const chatLastActivity = (chat: Chat): number => {
  if (chat.lastMessage?.timestamp) return chat.lastMessage.timestamp
  const iso = chat.lastMessage?.createdAt ?? chat.updatedAt ?? chat.createdAt
  const millis = iso ? DateTime.fromISO(iso).toMillis() : NaN
  return Number.isNaN(millis) ? 0 : millis
}

export const sortChatsByLastMessage = (chatsMap: Record<string, Chat>): Chat[] =>
  Object.values(chatsMap).sort((a, b) => chatLastActivity(b) - chatLastActivity(a))

export const getChatAreaLabel = (chat: Chat): string | undefined => {
  const areas = chat?.areas ?? []
  if (!areas.length) return undefined
  return areas.length > 1 ? `${areas[0]?.name} +${areas.length - 1}` : areas[0]?.name
}

export enum MessageSide {
  /** Bot bubble, left. */
  Bot = 'bot',
  /** Viewer's own message, right. */
  Own = 'own',
  /** Anyone else (support operators, other participants), left. */
  Other = 'other',
}

const SUPPORT_ROLES = [ChatRoleEnum.OPERATOR, ChatRoleEnum.SUPERVISOR, ChatRoleEnum.TENANT_ADMIN, ChatRoleEnum.ADMIN]

/**
 * Which side a message renders on, from the portal viewer's perspective.
 * Decided by identity, not by the sender's (server-stamped) global role, so an
 * operator/supervisor chatting with the bot still sees their own messages on the
 * right. Precedence: bot < own < guest-own < other.
 */
export const getMessageSide = (
  message: Pick<ChatMessage, 'senderId' | 'chatSenderRole'>,
  currentUserId: string | undefined,
  isLogged: boolean,
): MessageSide => {
  if (message.chatSenderRole === ChatRoleEnum.BOT) return MessageSide.Bot
  if (currentUserId && message.senderId === currentUserId) return MessageSide.Own
  // Guests have no id: their own messages are anything that isn't the bot or a support operator.
  if (!isLogged && !SUPPORT_ROLES.includes(message.chatSenderRole)) return MessageSide.Own
  return MessageSide.Other
}

export const findParticipantsByRole = (chat: Chat, chatRole: ChatRoleEnum) => {
  const currentChatParticipants = chat?.participants || []
  return currentChatParticipants.find((participant) => participant.role === chatRole)
}

export const findCurrentChatParticipantsByRole = (chatRole: ChatRoleEnum) => {
  const state = store.getState()
  const currentChat = messagesSelectors.currentChat(state)
  const currentChatParticipants = currentChat?.participants || []
  return currentChatParticipants.find((participant) => participant.role === chatRole)
}

export const chatIsToRead = (chat: Chat, currentUser?: User) => {
  const currentUserParticipant = _.find(chat?.participants, (p) => p?.userId === currentUser?._id)
  if (!currentUserParticipant || _.isNil(chat.lastMessage)) return false
  if (!currentUser) return false
  if (chat.responseMode === ChatResponseModeEnum.BOT) {
    return false
  }

  const currentUserLastReadTimeStamp = Number(_.get(currentUserParticipant, 'lastReadTimestamp', 0))
  const lastMessageTimestamp = _.get(chat, 'lastMessage.timestamp', 0)
  const lastMessageUserId = _.get(chat, 'lastMessage.senderId')

  // console.debug({
  //   currentUserParticipant,
  //   currentUserLastReadTimeStamp,
  //   currentUserLastReadTimeStampDate: DateTime.fromMillis(currentUserLastReadTimeStamp).toFormat(defaultDateTimeFormat),
  //   lastMessageTimestamp,
  //   lastMessageTimestampDate: DateTime.fromMillis(lastMessageTimestamp).toFormat(defaultDateTimeFormat),
  //   lastMessageUserId
  // })

  if (currentUserLastReadTimeStamp < lastMessageTimestamp && currentUser._id !== lastMessageUserId) {
    // console.debug('not read')
    return true
  } else {
    // console.debug('read')
    return false
  }
}
